import { CellProps, Column } from 'react-table';
import { Dokument } from '../../shared/types';
import dayjs from 'dayjs';
import TextItem from '../../shared/ui-components/TextItem/TextItem';
import styles from './DokumentSilageergebnisse.module.scss';
import ButtonGroup, { ButtonGroupAlignment } from '../../shared/ui-components/ButtonGroup/ButtonGroup';
import IconButton from '../../shared/ui-components/IconButton/IconButton';
import iconDownload from '../../assets/icon-file-download.png';
import { ReactComponent as DownloadIcon } from '../../assets/icon-download.svg';
import { deselectDokument, dokumentDownload, DokumentSelectors, selectDokument } from '../../store/DokumentSilageergebnisse.store';
import { useDispatch, useSelector } from 'react-redux';
import { Breakpoint, useBreakpoint } from '../../shared/hooks/useBreakpoint';
import { ReactElement } from 'react';
import ButtonIcon from '../../shared/ui-components/Button/ButtonIcon';
import NonFormikCheckbox from '../../shared/ui-components/CheckBox/NonFormikCheckbox';

export const createDokumentenCheckBox = (): Column<Dokument> => {
    return {
        id: 'checkBox',
        Header: '',
        accessor: 'id',
        disableSortBy: true,
        Cell: (cellProps: CellProps<Dokument>): JSX.Element => {
            const dispatch = useDispatch();
            const isMobile = useBreakpoint(Breakpoint.MOBILE);
            const cell = cellProps.cell;
            const value = cellProps.value;
            const isCurrentCheckBoxChecked = useSelector(DokumentSelectors.isDokumentChecked(value));
            if (!value) return <></>;

            const onChange = (checked: boolean): void => {
                if (checked) {
                    dispatch(selectDokument({ dokumentId: value }));
                } else {
                    dispatch(deselectDokument({ dokumentId: value }));
                }
            };

            return isMobile ? (
                <div className={styles._checkbox}>
                    <NonFormikCheckbox
                        label="Zum Download markieren"
                        name="checkdownload"
                        id={cell.value}
                        checked={isCurrentCheckBoxChecked}
                        placement="left"
                        onFieldChange={onChange}
                    />
                </div>
            ) : (
                <NonFormikCheckbox
                    name="checkdownload"
                    id={cell.value}
                    checked={isCurrentCheckBoxChecked}
                    placement="left"
                    onFieldChange={onChange}
                />
            );
        },
    };
};

export const createBelegNummerColumn = (): Column<Dokument> => {
    return {
        id: 'dokumentBelegNummer',
        Header: 'Belegnummer',
        accessor: 'belegNummer',
        disableSortBy: true,
        Cell: (cellProps: CellProps<Dokument>): ReactElement => {
            const isMobile = useBreakpoint(Breakpoint.MOBILE);
            const cell = cellProps.cell;
            if (!cell.value) return <></>;

            return isMobile ? <TextItem label="Belegnummer" text={cell.value} compressed /> : <>{cell.value}</>;
        },
    };
};

export const createBelegDatumColumn = (): Column<Dokument> => {
    return {
        id: 'belegDatum',
        Header: 'Belegdatum',
        accessor: (row: Dokument): Date | undefined => {
            return row.belegDatum ? new Date(row.belegDatum) : undefined;
        },
        Cell: (cellProps: CellProps<Dokument>): ReactElement => {
            const isMobile = useBreakpoint(Breakpoint.MOBILE);

            const cell = cellProps.cell;
            if (!cell.value) return <></>;

            const parsedDate = dayjs(cell.value);
            const datum = parsedDate.format('DD.MM.YYYY');
            const uhrzeit = parsedDate.format('HH:mm') + ' Uhr';
            return isMobile ? (
                <TextItem label="Datum" text={datum + ' - ' + uhrzeit} compressed />
            ) : (
                <>
                    <div>{datum}</div>
                    <span className={styles._subInfo}>{uhrzeit}</span>
                </>
            );
        },
    };
};

export const createBeschreibungColumn = (): Column<Dokument> => {
    return {
        id: 'dokumentBeschreibung',
        Header: 'Beschreibung',
        accessor: 'beschreibung',
        disableSortBy: true,
        Cell: (cellProps: CellProps<Dokument>): ReactElement => {
            const isMobile = useBreakpoint(Breakpoint.MOBILE);
            const cell = cellProps.cell;
            if (!cell.value) return <></>;

            return isMobile ? <TextItem label="Beschreibung" text={cell.value} compressed /> : <>{cell.value}</>;
        },
    };
};

export const createDownloadLinkColumn = (): Column<Dokument> => {
    return {
        id: 'actions',
        Cell: (cellProps: CellProps<Dokument>): ReactElement => {
            const isMobile = useBreakpoint(Breakpoint.MOBILE);
            const dispatch = useDispatch();
            const cell = cellProps.cell;
            return isMobile ? (
                <ButtonGroup alignment={ButtonGroupAlignment.CENTER} isWithinCell={false}>
                    <ButtonIcon
                        data-cy={'download-dokument-button'}
                        test-id={'download-dokument-button'}
                        icon={<DownloadIcon title={'Dokument herunterladen'} stroke={'#6f6f6f'} fill={'#6f6f6f'} strokeWidth={1.5} />}
                        onClick={() => dispatch(dokumentDownload({ dokumentId: cell.value }))}
                        disabled={!cell.value}
                    >
                        Downloaden
                    </ButtonIcon>
                </ButtonGroup>
            ) : (
                <ButtonGroup alignment={ButtonGroupAlignment.LEFT} isWithinCell>
                    <IconButton
                        data-cy={'download-dokument-button'}
                        test-id={'download-dokument-button'}
                        icon={iconDownload}
                        alt="Dokument herunterladen"
                        disabled={!cell.value}
                        onClick={() => dispatch(dokumentDownload({ dokumentId: cell.value }))}
                    />
                </ButtonGroup>
            );
        },
        Header: '',
        accessor: 'id',
        disableSortBy: true,
    };
};
